import { createContext, useContext, useEffect, useState, useCallback } from "react";

export const APP_VERSION = "7.19.052";
// (v7.19.052) Safari reset-token login bug fixed — reset-confirm only opens for a backend-validated link.

const PwaContext = createContext(null);
const DISMISS_KEY = "vpw_install_dismissed";

export const PwaProvider = ({ children }) => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(
    typeof window !== "undefined" && window.matchMedia("(display-mode: standalone)").matches
  );
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");
  const [updateReady, setUpdateReady] = useState(false);
  const [waiting, setWaiting] = useState(null);
  const [swReady, setSwReady] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    let refreshing = false;
    navigator.serviceWorker.register("/service-worker.js").then((reg) => {
      setSwReady(true);
      setLastUpdate(new Date().toISOString());
      if (reg.waiting && navigator.serviceWorker.controller) { setWaiting(reg.waiting); setUpdateReady(true); }
      reg.addEventListener("updatefound", () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          if (nw.state === "installed" && navigator.serviceWorker.controller) {
            setWaiting(nw); setUpdateReady(true);
          }
        });
      });
    }).catch(() => {});
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return; refreshing = true; window.location.reload();
    });
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return false;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    if (outcome === "dismissed") dismissInstall();
    return outcome === "accepted";
  }, [deferred]); // eslint-disable-line

  const dismissInstall = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, "1"); setDismissed(true);
  }, []);

  const applyUpdate = useCallback(() => {
    if (waiting) waiting.postMessage("SKIP_WAITING");
    else window.location.reload();
  }, [waiting]);

  const checkForUpdate = useCallback(async () => {
    if (!("serviceWorker" in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg) { await reg.update(); setLastUpdate(new Date().toISOString()); }
  }, []);

  const clearPublicCache = useCallback(async () => {
    const reg = await navigator.serviceWorker?.getRegistration();
    reg?.active?.postMessage("CLEAR_PUBLIC_CACHE");
    if (window.caches) { const keys = await caches.keys(); await Promise.all(keys.map((k) => caches.delete(k))); }
  }, []);

  const canInstall = !!deferred && !installed && !dismissed;

  return (
    <PwaContext.Provider value={{
      version: APP_VERSION, canInstall, install, dismissInstall, installed, dismissed,
      updateReady, applyUpdate, swReady, lastUpdate, checkForUpdate, clearPublicCache,
      hasPrompt: !!deferred,
    }}>
      {children}
    </PwaContext.Provider>
  );
};

export const usePwa = () => useContext(PwaContext);
